class CopyButton extends HTMLElement {
    static buttonEventListeners = ['click', 'mouseenter', 'mouseleave'];

    #hasCopied = false;

    constructor() {
        super();

        this.hideTooltip = this.hideTooltip.bind(this);
    }

    connectedCallback() {
        this.button = this.querySelector('button');

        if (this.button) {
            if (!this.hasAttribute('target-id')) {
                console.warn('<sa-copy-element> is missing `target-id` attribute.');
                this.button.disabled = true;
            }
        } else {
            console.warn('Missing child button element.');
        }

        if (this.button.ariaLabel) {
            this.copyText = this.button.ariaLabel;
        }

        this.constructor.buttonEventListeners.forEach((event) => {
            this.button.addEventListener(event, this);
        });

        this.setupTooltip();
    }

    disconnectedCallback() {
        clearTimeout(this.timer);

        this.popper.destroy();

        this.constructor.buttonEventListeners.forEach((event) => {
            this.button.removeEventListener(event, this);
        });
    }

    handleEvent(event) {
        event.preventDefault();

        if (this.#hasCopied || this.button.disabled) {
            return;
        }

        switch (event.type) {
            case 'click':
                this.copyToClipboard();
                break;
            case 'mouseenter':
                this.showTooltip(this.copyText);
                break;
            case 'mouseleave':
                this.hideTooltip();
                break;
        }
    }

    get copyText() {
        return this.getAttribute('copy-text') ?? 'Copy to clipboard';
    }

    set copyText(value){
        this.setAttribute('copy-text', value);
    }

    get errorText() {
        return this.getAttribute('error-text') ?? 'Unable to copy text';
    }

    set errorText(value) {
        this.setAttribute('error-text', value);
    }

    get successText() {
        return this.getAttribute('success-text') ?? 'Copied to clipboard';
    }

    set successText(value) {
        this.setAttribute('success-text', value);
    }

    get targetId() {
        return this.getAttribute('target-id') ?? 'Copied to clipboard';
    }

    set targetId(value) {
        this.setAttribute('target-id', value);
    }

    async copyToClipboard() {
        clearTimeout(this.timer);

        try {
            const targetEl = document.getElementById(this.targetId);
            let text = '';

            if (targetEl.tagName === 'INPUT') {
                const input = (targetEl);
                text = input.value;
            } else {
                text = targetEl.textContent;
            }

            await navigator.clipboard.writeText(text);

            this.#hasCopied = true;

            this.showTooltip(this.successText);

            this.timer = setTimeout(this.hideTooltip, 2000);
        } catch {
            this.showTooltip(this.errorText);
        }
    }

    setupTooltip() {
        this.tooltip = document.createElement('div');
        this.tooltip.role = 'tooltip';
        this.tooltip.classList.add('sa-tooltip');
        this.tooltip.innerHTML = `
            <div class="sa-tooltip-arrow"></div>
            <div class="sa-tooltip-content"></div>
        `;

        this.append(this.tooltip);

        this.button.ariaExpanded = false;

        this.popper = Popper.createPopper(this.button, this.tooltip, {
            placement: 'auto',

            modifiers: [
                {
                    name: 'arrow',

                    options: {
                        element: '.sa-tooltip-arrow',
                        padding: 5
                    }
                }, {
                    name: 'offset',
                    options: {offset: [0, 7]}
                }
            ]
        });
    }

    hideTooltip() {
        this.tooltip.classList.remove('sa-tooltip-visible');

        this.button.ariaExpanded = false;
        this.button.ariaLabel = this.copyText;

        this.#hasCopied = false;
    }

    showTooltip(text) {
        this.tooltip.querySelector('.sa-tooltip-content').textContent = text;

        this.tooltip.classList.add('sa-tooltip-visible');

        this.button.ariaExpanded = true;
        this.button.ariaLabel = text;

        this.popper.update();
    }
}

customElements.define('sa-copy-button', CopyButton);
